"use client";

import { useRef } from "react";
import { Eyebrow, SectionShell, Terminal, useBandRowAlign, useWholeRowBlocks } from "@/components/sections/kit";
import { useEnterStagger, useMaskedReveal } from "@/components/sections/kit/motion";
import { DISCLOSURE_CHECKS, SECURITY_TXT } from "./data";
import PinkCheck from "./PinkCheck";

/**
 * Responsible disclosure — the RFC 9116 security.txt, previewed exactly as
 * the real file ships at /.well-known/security.txt (public/, same values).
 */
export default function DisclosureSection() {
  const rootRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useMaskedReveal(copyRef, { heading: "h2" });
  useEnterStagger(copyRef, "[data-check-row]", { y: 14, delay: 0.15 });
  useEnterStagger(rootRef, "[data-sec-pane]", { y: 26 });

  return (
    <SectionShell
      id="disclosure"
      className="scroll-mt-[var(--grid-cell,72px)] md:pt-px md:pb-px"
    >
      <div ref={rootRef}>
        {/* One block — claim and file pane share a single clearing. */}
        <div
          data-grid-block
          className="grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_520px] lg:gap-16"
        >
          <div ref={copyRef} className="min-w-0">
            <div data-head-sub className="flex">
              <Eyebrow>Responsible disclosure</Eyebrow>
            </div>
            <h2 className="mt-5 text-[clamp(30px,4vw,48px)] font-bold leading-[1.06] tracking-[-0.027em]">
              security.txt, published.
            </h2>
            <p
              data-head-sub
              className="mt-[18px] max-w-[560px] text-[clamp(16px,1.4vw,19px)] leading-[1.6] text-muted"
            >
              RFC 9116, at the well-known path — so a researcher who finds
              something reaches a human in minutes, not a contact form.
            </p>
            <div className="mt-6 flex flex-col gap-3.5">
              {DISCLOSURE_CHECKS.map((check) => (
                <div key={check} data-check-row>
                  <PinkCheck>{check}</PinkCheck>
                </div>
              ))}
            </div>
          </div>

          <div data-sec-pane className="min-w-0">
            <Terminal path="rii.link/.well-known/security.txt">
              <div className="flex flex-col gap-[7px] text-[12.5px] leading-[1.5] [overflow-wrap:anywhere]">
                {SECURITY_TXT.map((line) => (
                  <div key={line.k}>
                    <span className="text-data-cyan">{line.k}:</span>{" "}
                    <span className="text-[#e8e8f0]">{line.v}</span>
                  </div>
                ))}
                <div className="mt-2 text-[#73738a]">
                  # this preview is the spec — the real file ships with the page
                </div>
              </div>
            </Terminal>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
